import React, { useState } from 'react';
import { BoldIcon, ItalicIcon, UnderlineIcon, StrikethroughIcon, LinkIcon, CodeIcon, QuoteIcon, ListIcon, ListOrderedIcon, AlignLeftIcon, GripVerticalIcon, PencilIcon, TrashIcon, PlusIcon, SmartphoneIcon, CreditCardIcon } from 'lucide-react';
type Section = 'cartes' | 'famille' | 'residences' | 'vehicules' | 'animaux';
type Language = 'fr' | 'en';
interface Justificatif {
  id: string;
  type: string;
  description: string;
}
interface RefusalReason {
  id: string;
  titre: string;
  descriptionInterne: string;
  motifCitoyen: string;
}
interface InstructionField {
  label: string;
  content: string;
}
interface CardElement {
  id: string;
  label: string;
  enabled: boolean;
}
interface SectionState {
  justificatifs: Justificatif[];
  instructions: InstructionField[];
  guide: string;
  refusalLang: Language;
  refusals: RefusalReason[];
}
const SECTION_LABELS: Record<Section, string> = {
  cartes: 'Cartes',
  famille: 'Famille',
  residences: 'Propriétés',
  vehicules: 'Véhicules',
  animaux: 'Animaux'
};
const SECTION_ORDER: Section[] = ['cartes', 'famille', 'residences', 'vehicules', 'animaux'];
const INITIAL_STATE: Record<Section, SectionState> = {
  cartes: {
    justificatifs: [{
      id: '1',
      type: 'permis',
      description: 'Permis de conduire valide'
    }, {
      id: '2',
      type: 'carte_id',
      description: "Carte d'identité avec photo"
    }],
    instructions: [{
      label: 'Introduction',
      content: 'Veuillez remplir vos renseignements personnels. Ces données seront validées par le personnel de la Ville.'
    }, {
      label: "Instructions d'adhésion à la carte",
      content: "Une carte citoyenne est requise afin de vous doter d'un abonnement."
    }, {
      label: 'Pièces justificatives',
      content: "Documents acceptés :\n• Carte d'identité avec photo, nom et adresse complète (ex: permis de conduire). Recto ET Verso\nou\n• Une carte d'identité avec photo SANS adresse complète (ex : carte d'assurance maladie) ET un document officiel d'un palier gouvernemental (provincial ou fédéral) ou un compte de taxes (municipales ou scolaires) de l'année courante ou précédente."
    }, {
      label: 'Confirmation',
      content: "Veuillez vérifier que vos informations sont exactes avant de confirmer l'envoi."
    }, {
      label: "Texte sous le bouton 'Adhérer'",
      content: "Vous n'avez jamais possédé de carte à la Ville de Windsor."
    }, {
      label: "Texte sous le bouton 'Lier'",
      content: 'Vous avez déjà possédé une carte à la Ville de Windsor.'
    }],
    guide: "Documents acceptés :\n• Carte d'identité avec photo, nom et adresse complète (ex: permis de conduire). Recto ET Verso\nou\n• Une carte d'identité avec photo SANS adresse complète (ex : carte d'assurance maladie) ET un document officiel d'un palier gouvernemental (provincial ou fédéral) ou un compte de taxes (municipales ou scolaires) de l'année courante ou précédente.",
    refusalLang: 'fr',
    refusals: [{
      id: '1',
      titre: 'Carte citoyenne - Mauvaise preuve - Adulte',
      descriptionInterne: 'Carte citoyenne - Mauvaise preuve - Adulte',
      motifCitoyen: "La preuve de résidence n'est pas valide. Les preuves acceptées sont le permis de conduire (recto et verso). Si vous n'en possédez pas, vous devez nous faire parvenir deux documents: une carte d'identité avec photo (ex : carte d'assurance maladie) ET un document officiel d'un palier gouvernemental (provincial ou fédéral), ou un compte de taxes (municipales ou scolaires) de moins de trois mois dont votre adresse de Windsor et votre nom sont inscrits dessus. Nous vous invitons à communiquer avec Services Windsor pour de plus amples informations par téléphone au 819 845-7888 ou par courriel à services@windsor.ca."
    }, {
      id: '2',
      titre: 'Carte citoyenne - Aucune preuve avec photo',
      descriptionInterne: 'Carte citoyenne - Aucune preuve avec photo',
      motifCitoyen: "Il manque une pièce d'identité avec photo. Les preuves acceptées sont le permis de conduire (recto et verso). Si vous n'en possédez pas, vous devez nous faire parvenir deux documents: une carte d'identité avec photo (ex : carte d'assurance maladie) ET un document officiel d'un palier gouvernemental (provincial ou fédéral), ou un compte de taxes (municipales ou scolaires) de moins de trois mois dont votre adresse de Windsor est inscrite dessus. Nous vous invitons à communiquer avec Services Windsor pour de plus amples informations par téléphone au 819 845-7888 ou par courriel à services@windsor.ca."
    }]
  },
  famille: {
    justificatifs: [{
      id: '1',
      type: 'permis',
      description: 'Permis de conduire valide'
    }],
    instructions: [{
      label: 'Introduction',
      content: 'Veuillez remplir les informations de votre famille.'
    }, {
      label: 'Pièces justificatives',
      content: 'Documents requis pour la validation familiale.'
    }],
    guide: 'Guide pour valider les informations familiales.',
    refusalLang: 'fr',
    refusals: [{
      id: '1',
      titre: 'Famille - Documents incomplets',
      descriptionInterne: 'Famille - Documents incomplets',
      motifCitoyen: 'Les documents fournis sont incomplets. Veuillez soumettre tous les justificatifs requis.'
    }]
  },
  residences: {
    justificatifs: [{
      id: '1',
      type: 'permis',
      description: 'Preuve de résidence'
    }],
    instructions: [{
      label: 'Introduction',
      content: 'Veuillez fournir une preuve de résidence valide.'
    }, {
      label: 'Pièces justificatives',
      content: 'Documents acceptés pour prouver votre résidence.'
    }],
    guide: 'Guide pour valider la résidence.',
    refusalLang: 'fr',
    refusals: [{
      id: '1',
      titre: 'Propriété - Preuve invalide',
      descriptionInterne: 'Propriété - Preuve invalide',
      motifCitoyen: "La preuve de propriété fournie n'est pas valide. Veuillez soumettre un document officiel récent."
    }]
  },
  vehicules: {
    justificatifs: [{
      id: '1',
      type: 'permis',
      description: "Certificat d'immatriculation (SAAQ)"
    }],
    instructions: [{
      label: 'Introduction',
      content: 'Veuillez enregistrer votre véhicule afin de bénéficier des services de stationnement de la Ville.'
    }, {
      label: 'Pièces justificatives',
      content: "Documents acceptés :\n• Certificat d'immatriculation de la SAAQ au nom du demandeur\nou\n• Contrat de location indiquant clairement le numéro de plaque et le titulaire."
    }],
    guide: "Documents acceptés :\n• Certificat d'immatriculation valide au nom du demandeur\n• Le numéro de plaque doit correspondre au véhicule déclaré.",
    refusalLang: 'fr',
    refusals: [{
      id: '1',
      titre: 'Véhicule - Immatriculation invalide',
      descriptionInterne: 'Véhicule - Immatriculation invalide',
      motifCitoyen: "Le certificat d'immatriculation fourni n'est pas valide ou ne correspond pas au demandeur. Veuillez soumettre un certificat d'immatriculation de la SAAQ à votre nom. Nous vous invitons à communiquer avec Services Windsor pour de plus amples informations par téléphone au 819 845-7888 ou par courriel à services@windsor.ca."
    }]
  },
  animaux: {
    justificatifs: [{
      id: '1',
      type: 'carte_id',
      description: 'Carnet de vaccination à jour'
    }],
    instructions: [{
      label: 'Introduction',
      content: 'Veuillez enregistrer votre animal afin d’obtenir sa médaille municipale, tel qu’exigé par le règlement de la Ville.'
    }, {
      label: 'Pièces justificatives',
      content: "Documents acceptés :\n• Carnet de vaccination à jour (rage)\n• Preuve de stérilisation (si applicable)\n• Photo de l'animal."
    }],
    guide: 'Documents acceptés :\n• Carnet de vaccination valide indiquant la vaccination contre la rage\n• Vérifier que la description de l’animal correspond aux documents fournis.',
    refusalLang: 'fr',
    refusals: [{
      id: '1',
      titre: 'Animal - Vaccination non valide',
      descriptionInterne: 'Animal - Vaccination non valide',
      motifCitoyen: "La preuve de vaccination fournie n'est pas valide ou est expirée. Veuillez soumettre un carnet de vaccination à jour incluant la vaccination contre la rage. Nous vous invitons à communiquer avec Services Windsor pour de plus amples informations par téléphone au 819 845-7888 ou par courriel à services@windsor.ca."
    }]
  }
};
export function BcitiIdConfigurer() {
  const [activeSection, setActiveSection] = useState<Section>('cartes');
  const [sections, setSections] = useState<Record<Section, SectionState>>(INITIAL_STATE);
  // Cartes-only card printing state
  const [cartesPrintEnabled, setCartesPrintEnabled] = useState(true);
  const [cardElements, setCardElements] = useState<CardElement[]>([{
    id: 'nom',
    label: 'Nom/Prénom',
    enabled: true
  }, {
    id: 'dob',
    label: 'Date de naissance',
    enabled: true
  }, {
    id: 'photo',
    label: 'Photo',
    enabled: true
  }, {
    id: 'adresse',
    label: 'Adresse',
    enabled: true
  }, {
    id: 'numero',
    label: 'Numéro',
    enabled: true
  }, {
    id: 'barcode',
    label: 'Barcode ou QR code',
    enabled: true
  }, {
    id: 'statut',
    label: 'Statut résident ou non-résident',
    enabled: true
  }, {
    id: 'expiration',
    label: "Date d'expiration",
    enabled: true
  }, {
    id: 'logo',
    label: 'Logo ville',
    enabled: true
  }, {
    id: 'cutting',
    label: 'Aide au découpage (pointillés)',
    enabled: true
  }]);
  const updateSection = (section: Section, patch: Partial<SectionState>) => {
    setSections(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        ...patch
      }
    }));
  };
  const addJustificatif = (section: Section) => {
    const newJustif: Justificatif = {
      id: Date.now().toString(),
      type: 'permis',
      description: ''
    };
    updateSection(section, {
      justificatifs: [...sections[section].justificatifs, newJustif]
    });
  };
  const removeJustificatif = (section: Section, id: string) => {
    updateSection(section, {
      justificatifs: sections[section].justificatifs.filter(j => j.id !== id)
    });
  };
  const updateJustificatif = (section: Section, id: string, field: 'type' | 'description', value: string) => {
    updateSection(section, {
      justificatifs: sections[section].justificatifs.map(j => j.id === id ? {
        ...j,
        [field]: value
      } : j)
    });
  };
  const addRefusal = (section: Section) => {
    const newRefusal: RefusalReason = {
      id: Date.now().toString(),
      titre: '',
      descriptionInterne: '',
      motifCitoyen: ''
    };
    updateSection(section, {
      refusals: [...sections[section].refusals, newRefusal]
    });
  };
  const removeRefusal = (section: Section, id: string) => {
    updateSection(section, {
      refusals: sections[section].refusals.filter(r => r.id !== id)
    });
  };
  const toggleCardElement = (id: string) => {
    setCardElements(cardElements.map(el => el.id === id ? {
      ...el,
      enabled: !el.enabled
    } : el));
  };
  const renderToolbar = () => <div className="flex items-center gap-1 p-2 bg-slate-50 border border-slate-200 rounded-t-lg flex-wrap" data-id="element-789" data-mp="rlp52nnurec2">
      <select className="text-xs border border-slate-300 rounded px-2 py-1 bg-white" data-id="element-790" data-mp="dukba2761g9n">
        <option data-id="element-791" data-mp="huugvdr1q4nz">Sans Serif</option>
      </select>
      <select className="text-xs border border-slate-300 rounded px-2 py-1 bg-white ml-2" data-id="element-792" data-mp="htjf1jh5v6zi">
        <option data-id="element-793" data-mp="3u08gv4003pb">Normal</option>
      </select>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-794" data-mp="8uegwe9ihfnv">
        <button className="p-1 hover:bg-slate-200 rounded" title="H1" data-id="element-795" data-mp="8sbj3824ai8l">
          <span className="text-xs font-bold" data-id="element-796" data-mp="3r23wa6dr6la7">H₁</span>
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" title="H2" data-id="element-797" data-mp="3v2xkz1z0s445">
          <span className="text-xs font-bold" data-id="element-798" data-mp="1p97d1w9owcf0">H₂</span>
        </button>
      </div>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-799" data-mp="1p73778s8d3c3">
        <button className="p-1 hover:bg-slate-200 rounded" title="Bold" data-id="element-800" data-mp="1t8f1vspyoer5">
          <BoldIcon className="w-4 h-4" data-id="element-801" data-mp="3dtd3rb3plc2x" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" title="Italic" data-id="element-802" data-mp="39r8c2hwtmpix">
          <ItalicIcon className="w-4 h-4" data-id="element-803" data-mp="39p5g7h8zm26a" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" title="Underline" data-id="element-804" data-mp="3nj530imr6tdq">
          <UnderlineIcon className="w-4 h-4" data-id="element-805" data-mp="3iremfux839dv" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" title="Strikethrough" data-id="element-806" data-mp="1b56qit6lcxr">
          <StrikethroughIcon className="w-4 h-4" data-id="element-807" data-mp="3shq40k9txh38" />
        </button>
      </div>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-808" data-mp="3sjss2yrf905b">
        <button className="p-1 hover:bg-slate-200 rounded" title="Link" data-id="element-809" data-mp="ab1y4s6ygpwi">
          <LinkIcon className="w-4 h-4" data-id="element-810" data-mp="5e1v6q5ek39m" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" title="Code" data-id="element-811" data-mp="2c2yag40y63sc">
          <CodeIcon className="w-4 h-4" data-id="element-812" data-mp="2c5aseh6pge5e" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" title="Quote" data-id="element-813" data-mp="2756jpdc1gnpq">
          <QuoteIcon className="w-4 h-4" data-id="element-814" data-mp="29jbdwj4cxjbk" />
        </button>
      </div>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-815" data-mp="29l4m42umcblo">
        <button className="p-1 hover:bg-slate-200 rounded" title="Text color" data-id="element-816" data-mp="2dikb5xylvzbv">
          <span className="text-xs font-bold" data-id="element-817" data-mp="2ijzthi9efrqk">A</span>
        </button>
      </div>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-818" data-mp="2nhi4de6z8h17">
        <button className="p-1 hover:bg-slate-200 rounded" title="Bullet list" data-id="element-819" data-mp="1pvay7zv0ygmq">
          <ListIcon className="w-4 h-4" data-id="element-820" data-mp="1utmb6jgm9796" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" title="Numbered list" data-id="element-821" data-mp="1uufc33sw92je">
          <ListOrderedIcon className="w-4 h-4" data-id="element-822" data-mp="1zqvvxlt28uqo" />
        </button>
      </div>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-823" data-mp="24kgvhiaaxwrz">
        <button className="p-1 hover:bg-slate-200 rounded" title="Align" data-id="element-824" data-mp="1739w8omyea93">
          <AlignLeftIcon className="w-4 h-4" data-id="element-825" data-mp="1c2ut8tikc8j6" />
        </button>
      </div>
    </div>;
  const renderJustificatifs = (section: Section) => {
    const justifs = sections[section].justificatifs;
    return <div className="space-y-4" data-id="element-826" data-mp="zkipqavm7o0x">
        <div className="flex items-center justify-between" data-id="element-827" data-mp="zlbn4l8op16e">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-828" data-mp="ul60xkl6o6vp">
            Pièces justificatives
          </label>
          <button onClick={() => addJustificatif(section)} className="text-sm text-[#4F46E5] hover:text-[#4338CA] font-medium flex items-center gap-1" data-id="element-829" data-mp="pmclvnq907tf">
            <PlusIcon className="w-4 h-4" data-id="element-830" data-mp="gspz2n40qewd" />
            Ajouter
          </button>
        </div>
        {justifs.map((justif, idx) => <div key={justif.id} className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-3" data-id="element-831" data-mp="6w4shxccovqf" data-mpi={idx}>
            <div className="flex items-center justify-between" data-id="element-832" data-mp="1vyulzfhmczw">
              <span className="text-sm font-medium text-slate-700" data-id="element-833" data-mp="3u12qts9ke9f2">
                Document {idx + 1}
              </span>
              <button onClick={() => removeJustificatif(section, justif.id)} className="text-slate-400 hover:text-red-600" data-id="element-834" data-mp="3jffrc9h20cbp">
                <TrashIcon className="w-4 h-4" data-id="element-835" data-mp="3afalw4vf7o06" />
              </button>
            </div>
            <div data-id="element-836" data-mp="3fe41c2u9pp2z">
              <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-837" data-mp="3fdlw4gpmo0ix">
                Type
              </label>
              <select value={justif.type} onChange={e => updateJustificatif(section, justif.id, 'type', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-838" data-mp="6zdbpvx9eg1u">
                <option value="permis" data-id="element-839" data-mp="pxg2af5ggl1r">Permis de conduire</option>
                <option value="carte_id" data-id="element-840" data-mp="pv3fnoor5mid">Carte d'identité</option>
                <option value="passeport" data-id="element-841" data-mp="uoofzndtiskq">Passeport</option>
              </select>
            </div>
            <div data-id="element-842" data-mp="141dpy8lu3bdw">
              <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-843" data-mp="140vmaqwuduvy">
                Description
              </label>
              <textarea value={justif.description} onChange={e => updateJustificatif(section, justif.id, 'description', e.target.value)} rows={2} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" placeholder="Description du document requis" data-id="element-844" data-mp="1dsi4563oqs7o" />
            </div>
          </div>)}
      </div>;
  };
  const renderInstructions = (section: Section) => {
    const instructions = sections[section].instructions;
    return <div className="space-y-6" data-id="element-845" data-mp="21hkio530w4xy">
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-846" data-mp="3vyauw673vebs">
          Messages d'instructions
        </label>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-847" data-mp="3vjplg0rl2lt">
          <div className="space-y-6" data-id="element-848" data-mp="8tv0zv6pjl65">
            {instructions.map((field, idx) => <div key={idx} data-id="element-849" data-mp="dkbch9qywwpv" data-mpi={idx}>
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-850" data-mp="dkl38zju77wm">
                  {field.label}
                </label>
                {renderToolbar()}
                <textarea value={field.content} onChange={e => {
              const updated = instructions.map((f, i) => i === idx ? {
                ...f,
                content: e.target.value
              } : f);
              updateSection(section, {
                instructions: updated
              });
            }} rows={3} className="w-full px-3 py-2 border border-slate-200 border-t-0 rounded-b-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-851" data-mp="nihctmx4lusm" />
              </div>)}
          </div>
          <div className="bg-slate-50 p-6 rounded-xl border border-slate-200" data-id="element-852" data-mp="2c2509l62ffz4">
            <div className="flex items-center gap-2 mb-4" data-id="element-853" data-mp="2c026qr5pf4pn">
              <SmartphoneIcon className="w-5 h-5 text-slate-600" data-id="element-854" data-mp="39okkuvtsgtqw" />
              <span className="text-sm font-semibold text-slate-700" data-id="element-855" data-mp="34r2cb6joogjf">
                Aperçu citoyen
              </span>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 space-y-4 max-h-[600px] overflow-y-auto" data-id="element-856" data-mp="2zqen8qf0m9ru">
              {instructions.map((field, idx) => <div key={idx} className="border-b border-slate-100 pb-3 last:border-0" data-id="element-857" data-mp="1ocau1mg94pnu" data-mpi={idx}>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase mb-1" data-id="element-858" data-mp="1k9e3n9kgzp9c">
                    {field.label}
                  </h4>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap" data-id="element-859" data-mp="fvr0e8m7soot">
                    {field.content}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </div>;
  };
  const renderGuide = (section: Section) => {
    const guide = sections[section].guide;
    return <div className="space-y-3" data-id="element-860" data-mp="13ka7wlxfwxfu">
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-861" data-mp="13j8uiasybyuy">
          Guide d'approbation
        </label>
        {renderToolbar()}
        <textarea value={guide} onChange={e => updateSection(section, {
        guide: e.target.value
      })} rows={6} className="w-full px-3 py-2 border border-slate-200 border-t-0 rounded-b-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-862" data-mp="ujlofbpa24vk" />
      </div>;
  };
  const renderRefusals = (section: Section) => {
    const refusals = sections[section].refusals;
    const lang = sections[section].refusalLang;
    const sectionLabel = SECTION_LABELS[section];
    return <div className="space-y-4" data-id="element-863" data-mp="1ytdpgjfblwit">
        <div className="flex items-center justify-between" data-id="element-864" data-mp="1ysc5tqk8o1ph">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-865" data-mp="23m6u77ash4uw">
            Motifs de refus — {sectionLabel}
          </h3>
          <button onClick={() => addRefusal(section)} className="px-4 py-2 text-sm font-medium text-[#4F46E5] border border-[#4F46E5] rounded-lg hover:bg-[#4F46E5] hover:text-white transition-colors flex items-center gap-2" data-id="element-866" data-mp="28jqd4kifm5t6">
            <PlusIcon className="w-4 h-4" data-id="element-867" data-mp="2mkatla0vjpji" />
            Ajouter un motif
          </button>
        </div>

        <div className="flex items-center gap-4 border-b border-slate-200" data-id="element-868" data-mp="o56isdqedqnh">
          <button onClick={() => updateSection(section, {
          refusalLang: 'fr'
        })} className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 ${lang === 'fr' ? 'text-[#4F46E5] border-[#4F46E5]' : 'text-slate-500 border-transparent hover:text-slate-700'}`} data-id="element-869" data-mp="s5qb9401063a">
            Français
          </button>
          <button onClick={() => updateSection(section, {
          refusalLang: 'en'
        })} className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 ${lang === 'en' ? 'text-[#4F46E5] border-[#4F46E5]' : 'text-slate-500 border-transparent hover:text-slate-700'}`} data-id="element-870" data-mp="20g8ntk8hesex">
            Anglais
          </button>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-id="element-871" data-mp="2j688zw17bnd2">
          <table className="w-full" data-id="element-872" data-mp="2x5zs2b6krtxx">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-873" data-mp="2x713igri6ig6">
              <tr data-id="element-874" data-mp="2s7y010r68kab">
                <th className="w-8" data-id="element-875" data-mp="2s8hiqjonbcpe"></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-876" data-mp="2s5bs300dc2rk">
                  Titre
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-877" data-mp="3gqqbt19zochd">
                  Description interne
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-878" data-mp="36tc3xq24sw0o">
                  Motif affiché au citoyen
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wide w-24" data-id="element-879" data-mp="2xt6z9nobobq7">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-880" data-mp="2o347fqsxt3tg">
              {refusals.map(refusal => <tr key={refusal.id} className="hover:bg-slate-50" data-id="element-881" data-mp="2a3utyyw48nt6">
                  <td className="px-2 py-3" data-id="element-882" data-mp="2a5pck6jx03u3">
                    <GripVerticalIcon className="w-4 h-4 text-slate-400 cursor-move" data-id="element-883" data-mp="2f2z1ll3e6dhe" />
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700" data-id="element-884" data-mp="270yflh96unx1">
                    {refusal.titre}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700" data-id="element-885" data-mp="223orq61g6kbb">
                    {refusal.descriptionInterne}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 max-w-md truncate" data-id="element-886" data-mp="1tauwv09ipccc">
                    {refusal.motifCitoyen}
                  </td>
                  <td className="px-4 py-3 text-center" data-id="element-887" data-mp="1od36iaoitya9">
                    <div className="flex items-center justify-center gap-2" data-id="element-888" data-mp="1jbvxy9ek0v2j">
                      <button className="text-slate-400 hover:text-[#4F46E5]" data-id="element-889" data-mp="1edcd6cbp5pbr">
                        <PencilIcon className="w-4 h-4" data-id="element-890" data-mp="1eff0golgt4cs" />
                      </button>
                      <button onClick={() => removeRefusal(section, refusal.id)} className="text-slate-400 hover:text-red-600" data-id="element-891" data-mp="1adjxzgrsskxf">
                        <TrashIcon className="w-4 h-4" data-id="element-892" data-mp="34qjjfqiv09cm" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>;
  };
  const renderCardPrinting = () => {
    return <div className="space-y-6" data-id="element-893" data-mp="108bzse9qf2la">
        <div className="flex items-center justify-between" data-id="element-894" data-mp="149e1d1r4fqqf">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-895" data-mp="148vyhm9yee9j">
            Impression de la carte
          </label>
          <button onClick={() => setCartesPrintEnabled(!cartesPrintEnabled)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${cartesPrintEnabled ? 'bg-[#4F46E5]' : 'bg-slate-300'}`} data-id="element-896" data-mp="chy4mevuonmh">
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${cartesPrintEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-897" data-mp="lk7h3aaoir2i" />
          </button>
        </div>

        {cartesPrintEnabled && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-898" data-mp="3t0fyp6plqgmg">
            <div className="space-y-4" data-id="element-899" data-mp="3ozmd4jzmtdwq">
              <h4 className="text-sm font-semibold text-slate-700" data-id="element-900" data-mp="3oykplg1fiyy4">
                Éléments sur la carte
              </h4>
              <div className="space-y-2" data-id="element-901" data-mp="6t7zyn96t7ze">
                {cardElements.map(element => <div key={element.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-id="element-902" data-mp="1vfvktrcvteb">
                    <span className="text-sm text-slate-700" data-id="element-903" data-mp="35coc9aackn7g">
                      {element.label}
                    </span>
                    <button onClick={() => toggleCardElement(element.id)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${element.enabled ? 'bg-[#4F46E5]' : 'bg-slate-300'}`} data-id="element-904" data-mp="3k243sshpcwo7">
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${element.enabled ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-905" data-mp="2mkcc8u761y0h" />
                    </button>
                  </div>)}
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl border border-slate-200" data-id="element-906" data-mp="t49clu47qu9t">
              <div className="flex items-center gap-2 mb-4" data-id="element-907" data-mp="x4t70pxejjs9">
                <CreditCardIcon className="w-5 h-5 text-slate-600" data-id="element-908" data-mp="1kt2r9neyvzha" />
                <span className="text-sm font-semibold text-slate-700" data-id="element-909" data-mp="1krrnvopfotqp">
                  Aperçu de la carte
                </span>
              </div>
              <div className={`bg-gradient-to-br from-[#4F46E5] to-[#6366F1] rounded-xl p-6 shadow-lg relative ${cardElements.find(el => el.id === 'cutting')?.enabled ? 'border-2 border-dashed border-slate-400' : ''}`} style={{
            aspectRatio: '1.586/1'
          }} data-id="element-910" data-mp="1bruu58o6t4os">
                {cardElements.find(el => el.id === 'logo')?.enabled && <div className="absolute top-4 left-4" data-id="element-911" data-mp="w4gyxg12uczh">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center" data-id="element-912" data-mp="s0sqkv4mfdd6">
                      <span className="text-white font-bold text-xs" data-id="element-913" data-mp="n1frnc0h6fu8">LOGO</span>
                    </div>
                  </div>}
                <div className="flex flex-col h-full justify-between text-white" data-id="element-914" data-mp="i4z3ihta01h5">
                  <div className="flex items-start gap-4" data-id="element-915" data-mp="1fqd9xf4oscmz">
                    {cardElements.find(el => el.id === 'photo')?.enabled && <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center mt-16" data-id="element-916" data-mp="1613i80dr8vze">
                        <span className="text-xs" data-id="element-917" data-mp="112a01uniwgsw">Photo</span>
                      </div>}
                    <div className="flex-1 mt-16 space-y-1" data-id="element-918" data-mp="1yhw8n9roh4j7">
                      {cardElements.find(el => el.id === 'nom')?.enabled && <div className="text-sm font-bold" data-id="element-919" data-mp="1tpcxejggqlhk">Jean Dupont</div>}
                      {cardElements.find(el => el.id === 'dob')?.enabled && <div className="text-xs opacity-90" data-id="element-920" data-mp="261ou4335kxed">15 mars 1985</div>}
                      {cardElements.find(el => el.id === 'adresse')?.enabled && <div className="text-xs opacity-90" data-id="element-921" data-mp="2g0o0ff0g4gy7">
                          11 rue St-Georges, Windsor
                        </div>}
                    </div>
                  </div>
                  <div className="flex items-end justify-between" data-id="element-922" data-mp="2oxolpcs7xyxf">
                    <div className="space-y-1" data-id="element-923" data-mp="2trsvs4ws6n2q">
                      {cardElements.find(el => el.id === 'numero')?.enabled && <div className="text-xs font-mono" data-id="element-924" data-mp="33oxj482d4ol4">No: 123456789</div>}
                      {cardElements.find(el => el.id === 'statut')?.enabled && <div className="text-xs" data-id="element-925" data-mp="37nwp7vztyre5">Résident</div>}
                      {cardElements.find(el => el.id === 'expiration')?.enabled && <div className="text-xs" data-id="element-926" data-mp="jaev6oxjpxms">Exp: 31/12/2025</div>}
                    </div>
                    {cardElements.find(el => el.id === 'barcode')?.enabled && <div className="w-16 h-16 bg-white/20 rounded flex items-center justify-center" data-id="element-927" data-mp="x0i9mtq6on1o">
                        <span className="text-xs" data-id="element-928" data-mp="11zlead294r94">QR</span>
                      </div>}
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>;
  };
  const renderSectionContent = () => {
    return <div className="space-y-8" data-id="element-929" data-mp="s7fdcdu0uzhs">
        {renderJustificatifs(activeSection)}
        {renderInstructions(activeSection)}
        {renderGuide(activeSection)}
        {renderRefusals(activeSection)}
        {activeSection === 'cartes' && renderCardPrinting()}
      </div>;
  };
  return <div className="flex gap-6" data-id="element-930" data-mp="35eez69w812xc">
      {/* Section Switcher */}
      <div className="w-48 flex-shrink-0" data-id="element-931" data-mp="30glw3pq7sp1a">
        <div className="bg-white rounded-xl border border-slate-200 p-2 space-y-1 sticky top-4" data-id="element-932" data-mp="30fkibdhtdofv">
          {SECTION_ORDER.map(section => <button key={section} onClick={() => setActiveSection(section)} className={`w-full px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${activeSection === section ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-933" data-mp="3a89i3b405dvd">
              {SECTION_LABELS[section]}
            </button>)}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 bg-white rounded-xl border border-slate-200 p-8" data-id="element-934" data-mp="as32zi2eh87d">
        {renderSectionContent()}
      </div>
    </div>;
}